import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

// Create axios instance with default config
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ========== AUTHENTICATION APIs ==========

export const registerUser = async (userData) => {
  try {
    const response = await axiosInstance.post('/auth/register', userData);
    return response.data;
  } catch (error) {
    console.error('Registration error:', error);
    throw error;
  }
};

export const login = async (username, password) => {
  try {
    const response = await axiosInstance.post('/auth/login', {
      username,
      password,
    });
    return response.data;
  } catch (error) {
    console.error('Login error:', error);
    throw error.response?.data || error;
  }
};

export const checkUsername = async (username) => {
  try {
    const response = await axiosInstance.get(`/auth/check-username/${username}`);
    return response.data;
  } catch (error) {
    console.error('Username check error:', error);
    throw error;
  }
};

export const deleteOwnAccount = async (username) => {
  try {
    const response = await axiosInstance.delete(`/auth/delete-account/${username}`);
    return response.data;
  } catch (error) {
    console.error('Delete account error:', error);
    throw error.response?.data || error;
  }
};

// ========== PROFILE APIs ==========

export const getProfile = async (username) => {
  try {
    const response = await axiosInstance.get(`/profile/${username}`);
    return response.data;
  } catch (error) {
    console.error('Get profile error:', error);
    throw error;
  }
};

export const saveProfile = async (profileData) => {
  try {
    const response = await axiosInstance.post('/profile', profileData);
    return response.data;
  } catch (error) {
    console.error('Save profile error:', error);
    throw error;
  }
};

export const generateAIDescription = async (username) => {
  try {
    const response = await axiosInstance.post(`/profile/${username}/generate-ai-description`);
    return response.data;
  } catch (error) {
    console.error('Generate AI description error:', error);
    throw error;
  }
};

// ========== ENTRY APIs (UPDATED FOR USER ISOLATION) ==========

export const createEntry = async (entry) => {
  try {
    const response = await axiosInstance.post('/entries', entry);
    return response.data;
  } catch (error) {
    console.error('Create entry error:', error);
    throw error;
  }
};

export const getAllEntriesByUsername = async (username) => {
  try {
    const response = await axiosInstance.get(`/entries/user/${username}`);
    return response.data;
  } catch (error) {
    console.error('Get entries error:', error);
    throw error;
  }
};

export const getEntriesByUsernameAndPartner = async (username, partnerName) => {
  try {
    const response = await axiosInstance.get(`/entries/user/${username}/partner/${partnerName}`);
    return response.data;
  } catch (error) {
    console.error('Get entries by partner error:', error);
    throw error;
  }
};

export const getEntryById = async (id, username) => {
  try {
    const response = await axiosInstance.get(`/entries/${id}`, {
      params: { username }
    });
    return response.data;
  } catch (error) {
    console.error('Get entry by ID error:', error);
    throw error;
  }
};

export const updateEntry = async (id, entry, username) => {
  try {
    const response = await axiosInstance.put(`/entries/${id}`, entry, {
      params: { username }
    });
    return response.data;
  } catch (error) {
    console.error('Update entry error:', error);
    throw error;
  }
};

export const deleteEntry = async (id, username) => {
  try {
    const response = await axiosInstance.delete(`/entries/${id}`, {
      params: { username }
    });
    return response.data;
  } catch (error) {
    console.error('Delete entry error:', error);
    throw error;
  }
};

// ========== REPORTS APIs (UPDATED FOR USER ISOLATION) ==========

export const getDailyReport = async (username, partnerName, date = null) => {
  try {
    const url = date 
      ? `/reports/daily/${username}/${partnerName}?date=${date}` 
      : `/reports/daily/${username}/${partnerName}`;
    const response = await axiosInstance.get(url);
    return response.data;
  } catch (error) {
    console.error('Get daily report error:', error);
    throw error;
  }
};

export const getWeeklyReport = async (username, partnerName) => {
  try {
    const response = await axiosInstance.get(`/reports/weekly/${username}/${partnerName}`);
    return response.data;
  } catch (error) {
    console.error('Get weekly report error:', error);
    throw error;
  }
};

export const getMonthlyReport = async (username, partnerName) => {
  try {
    const response = await axiosInstance.get(`/reports/monthly/${username}/${partnerName}`);
    return response.data;
  } catch (error) {
    console.error('Get monthly report error:', error);
    throw error;
  }
};

export const getCombinedReport = async (username, partner1, partner2, period) => {
  try {
    const response = await axiosInstance.get('/reports/combined', {
      params: { username, partner1, partner2, period }
    });
    return response.data;
  } catch (error) {
    console.error('Get combined report error:', error);
    throw error;
  }
};

export const testAPI = async () => {
  try {
    const response = await axiosInstance.get('/reports/test');
    return response.data;
  } catch (error) {
    console.error('API test error:', error);
    throw error;
  }
};

// ========== AI FEEDBACK APIs ==========

// Generate AI feedback for individual report
export const generateIndividualFeedback = async (username, partnerName, period) => {
  try {
    const response = await axiosInstance.get(
      `/reports/feedback/${username}/${partnerName}`,
      {
        params: { period }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error generating individual feedback:', error);
    throw error;
  }
};

// Generate AI feedback for couple report
export const generateCoupleFeedback = async (username, partner1, partner2, period) => {
  try {
    const response = await axiosInstance.get(
      `/reports/feedback/couple/${username}`,
      {
        params: { partner1, partner2, period }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error generating couple feedback:', error);
    throw error;
  }
};

// ========== PRIVACY & PIN FUNCTIONS (UPDATED FOR INDEPENDENT PINs) ==========

/**
 * Set PIN for a specific gender
 * @param {string} username - User's username
 * @param {string} pin - 4-digit PIN
 * @param {string} gender - "male" or "female"
 */
export const setPin = async (username, pin, gender) => {
  try {
    const response = await axiosInstance.post('/privacy/set-pin', {
      username,
      pin,
      gender
    });
    return response.data;
  } catch (error) {
    console.error('Set PIN error:', error);
    throw error;
  }
};

/**
 * Verify PIN for a specific gender
 * @param {string} username - User's username
 * @param {string} pin - 4-digit PIN to verify
 * @param {string} gender - "male" or "female"
 */
export const verifyPin = async (username, pin, gender) => {
  try {
    const response = await axiosInstance.post('/privacy/verify-pin', {
      username,
      pin,
      gender
    });
    return response.data;
  } catch (error) {
    console.error('Verify PIN error:', error);
    throw error;
  }
};

/**
 * Reset PIN using password verification
 * @param {string} username - User's username
 * @param {string} password - User's account password
 * @param {string} newPin - New 4-digit PIN
 * @param {string} gender - "male" or "female"
 */
export const resetPin = async (username, password, newPin, gender) => {
  try {
    const response = await axiosInstance.post('/privacy/reset-pin', {
      username,
      password,
      newPin,
      gender
    });
    return response.data;
  } catch (error) {
    console.error('Reset PIN error:', error);
    throw error;
  }
};

/**
 * Check if user has PIN enabled for specific gender
 * @param {string} username - User's username
 * @param {string} gender - "male" or "female"
 */
export const hasPin = async (username, gender) => {
  try {
    const response = await axiosInstance.get(`/privacy/has-pin/${username}/${gender}`);
    return response.data;
  } catch (error) {
    console.error('Check PIN error:', error);
    throw error;
  }
};

/**
 * Check if user has both PINs enabled
 * @param {string} username - User's username
 * @returns {Object} { hasMalePin: boolean, hasFemalePin: boolean }
 */
export const checkBothPins = async (username) => {
  try {
    const [maleResponse, femaleResponse] = await Promise.all([
      axiosInstance.get(`/privacy/has-pin/${username}/male`),
      axiosInstance.get(`/privacy/has-pin/${username}/female`)
    ]);
    
    return {
      hasMalePin: maleResponse.data.hasPin,
      hasFemalePin: femaleResponse.data.hasPin
    };
  } catch (error) {
    console.error('Check both PINs error:', error);
    throw error;
  }
};

// Get privacy settings
export const getPrivacySettings = async (username) => {
  try {
    const response = await axiosInstance.get(`/privacy/settings/${username}`);
    return response.data;
  } catch (error) {
    console.error('Get privacy settings error:', error);
    throw error;
  }
};

// Update privacy settings
export const updatePrivacySettings = async (username, settings) => {
  try {
    const response = await axiosInstance.put(`/privacy/settings/${username}`, settings);
    return response.data;
  } catch (error) {
    console.error('Update privacy settings error:', error);
    throw error;
  }
};

/**
 * Get private entries for a specific partner
 * @param {string} ownerUsername - Owner of the entries
 * @param {string} partnerName - Partner name to filter by
 * @param {string} requestingUser - User requesting access
 * @param {string} pin - PIN for verification (optional, backend handles verification)
 */
export const getPrivateEntriesForPartner = async (ownerUsername, partnerName, requestingUser, pin) => {
  try {
    const response = await axiosInstance.get(
      `/privacy/private-entries/${ownerUsername}/${partnerName}`,
      {
        params: { requestingUser, pin }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Get private entries error:', error);
    throw error;
  }
};


// ========== NOTIFICATION APIs ==========
// ADD THESE FUNCTIONS TO THE BOTTOM OF YOUR EXISTING api.js FILE
// (paste above the "export default axiosInstance;" line)

export const getNotifications = async (username) => {
  try {
    const response = await axiosInstance.get(`/notifications/${username}`);
    return response.data;
  } catch (error) {
    console.error('Get notifications error:', error);
    throw error;
  }
};

export const getUnreadCount = async (username) => {
  try {
    const response = await axiosInstance.get(`/notifications/${username}/unread-count`);
    return response.data;
  } catch (error) {
    console.error('Get unread count error:', error);
    throw error;
  }
};

export const markNotificationAsRead = async (username, notificationId) => {
  try {
    const response = await axiosInstance.put(`/notifications/${username}/read/${notificationId}`);
    return response.data;
  } catch (error) {
    console.error('Mark notification as read error:', error);
    throw error;
  }
};

export const markAllNotificationsAsRead = async (username) => {
  try {
    const response = await axiosInstance.put(`/notifications/${username}/read-all`);
    return response.data;
  } catch (error) {
    console.error('Mark all notifications as read error:', error);
    throw error;
  }
};

// ========== FORGOT PASSWORD APIs ==========
// ADD THESE FUNCTIONS TO YOUR EXISTING api.js FILE
// (paste above the "export default axiosInstance;" line)

export const getSecurityQuestion = async (username) => {
  try {
    const response = await axiosInstance.get(`/auth/security-question/${username}`);
    return response.data;
  } catch (error) {
    console.error('Get security question error:', error);
    throw error;
  }
};

export const resetPassword = async (username, securityAnswer, newPassword, confirmPassword) => {
  try {
    const response = await axiosInstance.post('/auth/reset-password', {
      username,
      securityAnswer,
      newPassword,
      confirmPassword
    });
    return response.data;
  } catch (error) {
    console.error('Reset password error:', error);
    throw error;
  }
};

export const setSecurityQuestion = async (username, question, answer) => {
  try {
    const response = await axiosInstance.post('/auth/set-security-question', {
      username,
      question,
      answer
    });
    return response.data;
  } catch (error) {
    console.error('Set security question error:', error);
    throw error;
  }
};
// ========== PAYMENT & SUBSCRIPTION APIs ==========

export const initiatePayment = async (username, phoneNumber) => {
  try {
    const response = await axiosInstance.post('/payments/mpesa/initiate', {
      username,
      phoneNumber
    });
    return response.data;
  } catch (error) {
    console.error('Initiate payment error:', error);
    throw error;
  }
};

export const getSubscriptionStatus = async (username) => {
  try {
    const response = await axiosInstance.get(`/payments/subscription/status/${username}`);
    return response.data;
  } catch (error) {
    console.error('Get subscription status error:', error);
    throw error;
  }
};

export const checkPaymentStatus = async (checkoutRequestId) => {
  try {
    const response = await axiosInstance.get(`/payments/mpesa/status/${checkoutRequestId}`);
    return response.data;
  } catch (error) {
    console.error('Check payment status error:', error);
    throw error;
  }
};
export default axiosInstance;